import { css, Interpolation, Theme } from '@emotion/react';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, RadioField, Chip, SearchField, useFormField } from '@frontend/design-system';
import {
  FilterableCheckboxListItem,
  useFilterableCheckboxList,
} from '../filterable-checkbox-list/use-filterable-checkbox-list';

interface Props<T extends FilterableCheckboxListItem = FilterableCheckboxListItem> {
  fieldName: string;
  list?: T[];
  radioField: ReturnType<typeof useFormField<'text'>>;
  clearButtonTrackingId?: string;
  listStyle?: Interpolation<Theme>;
  Label?: React.ComponentType<{ item: T }>;
  hideClear?: boolean;
}

/**
 * A filterable radio list component that allows searching and selecting a single item from a list.
 * Similar to FilterableCheckboxList but for single selection with radio buttons.
 */
export const FilterableRadioList = <T extends FilterableCheckboxListItem = FilterableCheckboxListItem>({
  fieldName,
  list,
  radioField,
  clearButtonTrackingId,
  listStyle,
  Label,
  hideClear = false,
}: Props<T>) => {
  const { t } = useTranslation('schedule');
  const selectedValue = radioField.value || '';
  const selectedValues = selectedValue ? [selectedValue] : [];

  const { filteredList, searchField, isAnySelected, handleClear } = useFilterableCheckboxList<T>({
    list,
    selectedValues,
    onChange: (value) => {
      // For radio, we only care about the first selected value
      radioField.onChange({ value: value[0] || null, name: fieldName });
    },
  });

  return (
    <section>
      <SearchField
        name='search'
        {...searchField}
        // NOTE: keep this to prevent the popover from not allow typing in the search field
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      />
      {isAnySelected && !hideClear && (
        <Button variant='tertiary' onClick={handleClear} trackingId={clearButtonTrackingId}>
          {t('Clear')}
        </Button>
      )}
      {!filteredList?.length ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'schedule',
            header: t('No results found'),
            description: '',
          }}
        />
      ) : (
        <RadioField name={fieldName} label={''} {...radioField} css={listStyle}>
          {filteredList.map((item) => (
            <RadioField.Radio key={item.value} value={item.value} css={radioOptionStyle}>
              {Label ? <Label item={item} /> : item.label}
              {!!item.locationName && (
                <Chip.Location maxWidth='120px' css={{ marginLeft: theme.spacing(2) }}>
                  {item.locationName}
                </Chip.Location>
              )}
            </RadioField.Radio>
          ))}
        </RadioField>
      )}
    </section>
  );
};

const radioOptionStyle = css({
  margin: 0,
  transition: 'background-color 0.2s ease-out',
  cursor: 'pointer',
  borderRadius: theme.borderRadius.small,
  alignItems: 'center',
  '&:hover': { backgroundColor: theme.colors.neutral5, '> div::before': { transform: 'scale(1)' } },
  '> label': {
    flex: 1,
    cursor: 'pointer',
    padding: theme.spacing(1),
    margin: 0,
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  '> div': { margin: theme.spacing(1, 0, 1, 1) },
});
